// UV index: WHO/EPA exposure categories and EPA Envirofacts parsing.

import type { UvHour } from "./types";

export type UvLevel = "low" | "moderate" | "high" | "very-high" | "extreme";

export interface UvCategory {
  level: UvLevel;
  label: string;
  /** WHO Global Solar UV Index standard colour. */
  color: string;
  advice: string;
}

// Colours and advice from the WHO "Global Solar UV Index: A Practical Guide" (2002).
const CATS: UvCategory[] = [
  { level: "low", label: "Low", color: "#289500", advice: "No protection needed. Safe to be outside." },
  { level: "moderate", label: "Moderate", color: "#f7e400", advice: "Seek shade around midday. Shirt, sunscreen and hat." },
  { level: "high", label: "High", color: "#f85900", advice: "Seek shade around midday. Shirt, sunscreen and hat." },
  { level: "very-high", label: "Very high", color: "#d8001d", advice: "Avoid midday sun. Shirt, sunscreen and hat are a must." },
  { level: "extreme", label: "Extreme", color: "#6b49c8", advice: "Avoid being outside around midday. Full protection." },
];

export function uvCategory(uv: number): UvCategory {
  const v = Math.round(uv);
  if (v <= 2) return CATS[0]!;
  if (v <= 5) return CATS[1]!;
  if (v <= 7) return CATS[2]!;
  if (v <= 10) return CATS[3]!;
  return CATS[4]!;
}

/** "Broomfield, CO" → { city: "Broomfield", state: "CO" }. Null for anything else. */
export function cityStateFromLabel(label: string | null | undefined): { city: string; state: string } | null {
  const m = /^\s*([A-Za-z][A-Za-z .'-]{0,60}?)\s*,\s*([A-Za-z]{2})\s*$/.exec(label ?? "");
  if (!m) return null;
  return { city: m[1]!.trim(), state: m[2]!.toUpperCase() };
}

const MONTHS: Record<string, number> = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };

/**
 * Parse Envirofacts getEnvirofactsUVHOURLY JSON rows
 * ({ DATE_TIME: "Oct/22/2026 07 AM", UV_VALUE: 0 }, local time of the location).
 * Returns the first day present and its hours, or null if nothing usable.
 */
export function parseEpaUvHourly(rows: unknown): { date: string; hours: UvHour[] } | null {
  if (!Array.isArray(rows)) return null;
  const byDate = new Map<string, UvHour[]>();
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const dt = (r as Record<string, unknown>).DATE_TIME;
    const uv = Number((r as Record<string, unknown>).UV_VALUE);
    if (typeof dt !== "string" || !Number.isFinite(uv) || uv < 0 || uv > 25) continue;
    const m = /^([A-Za-z]{3})\/(\d{1,2})\/(\d{4}) (\d{1,2}) ?(AM|PM)$/i.exec(dt.trim());
    if (!m) continue;
    const month = MONTHS[m[1]!.toUpperCase()];
    if (!month) continue;
    let hour = Number(m[4]) % 12;
    if (m[5]!.toUpperCase() === "PM") hour += 12;
    const date = `${m[3]}-${String(month).padStart(2, "0")}-${String(Number(m[2])).padStart(2, "0")}`;
    const list = byDate.get(date) ?? [];
    list.push({ hour, uv });
    byDate.set(date, list);
  }
  const first = [...byDate.keys()].sort()[0];
  if (!first) return null;
  const hours = byDate.get(first)!.sort((a, b) => a.hour - b.hour);
  return { date: first, hours };
}

/** Peak hour of a day's forecast (first hour at the maximum). */
export function uvPeak(hours: UvHour[]): UvHour | null {
  let best: UvHour | null = null;
  for (const h of hours) if (!best || h.uv > best.uv) best = h;
  return best;
}

/** Hours (local) when UV is 3 or more — the WHO "protection required" threshold. */
export function protectionWindow(hours: UvHour[]): { from: number; to: number } | null {
  const hi = hours.filter((h) => h.uv >= 3);
  if (!hi.length) return null;
  return { from: hi[0]!.hour, to: hi[hi.length - 1]!.hour + 1 };
}
